import { Link } from 'expo-router';
import { type ReactNode } from 'react';
import { BodySmallRegular, YStack } from 'smoothie';

import { ProviderRecognitionFAQAnswer } from '../components/FAQ/ProviderRecognitionFAQAnswer';
import { SUPPORT_EMAIL } from './constants';

export interface FAQ {
    question: string;
    answer: string | ReactNode;
}

interface FAQCategory {
    category: string;
    faqs: FAQ[];
}

/**
 * Consolidated Cash, Card, banking, and membership FAQs. Benefits such as the
 * 2% cash back rate are membership-tier dependent, so they carry the
 * disclosure footnote.
 */
export const faqList: FAQCategory[] = [
    {
        category: 'Fruitful Cash & Card',
        faqs: [
            {
                question: 'What is Fruitful Cash & Card?',
                answer: (
                    <YStack gap={12}>
                        <BodySmallRegular>
                            The Fruitful Cash Account, issued by Emigrant Bank,
                            Member FDIC, is a high-yield interest bearing
                            deposit account. You earn interest on all your
                            money, whether you plan to save or spend it. It’s
                            the best of both worlds – the high earnings of a
                            savings account with the flexibility of a
                            traditional checking account.
                        </BodySmallRegular>
                        <BodySmallRegular>
                            Fruitful Card, issued by Emigrant Bank, Member FDIC,
                            is a secured charge card tied directly to your
                            Fruitful Cash Account, making it easy to spend
                            responsibly. Your transactions are reflected in your
                            available Cash Account balance, and you can earn up
                            to 2% cash back* on every purchase. See{' '}
                            <Link
                                target="_blank"
                                href="https://fruitful.com/legal/rewards-tc"
                            >
                                <BodySmallRegular textDecorationLine="underline">
                                    Fruitful Card Rewards Program Terms and
                                    Conditions
                                </BodySmallRegular>
                            </Link>{' '}
                            to learn more.
                        </BodySmallRegular>
                    </YStack>
                ),
            },
            {
                question: 'How does Fruitful Cash & Card work?',
                answer: "When you open a Fruitful Cash Account and get a Fruitful Card, the amount you deposit becomes your spending limit - just like the credit limit on a regular credit card.\n\nAs you use your Fruitful Card, your spending is reflected in your available cash balance. The funds you spend are then set aside in your Fruitful Cash Account, ensuring that your statement balance is covered at the end of the month. This way, you're always spending within your means, with no surprises.",
            },
            {
                question:
                    'What happens to the money in my Fruitful Cash Account when I use my Fruitful Card?',
                answer: 'Each time you use your Fruitful Card to make a purchase, your spending is reflected in your Fruitful Cash Account available balance. The funds used for those purchases are then set aside (and still earning interest) to cover your transaction. The reserved funds are then used to pay off the statement balance every month.',
            },
            {
                question: "What's a secured charge card?",
                answer: 'A secured charge card is a type of credit card where your spending is capped by the amount of cash you hold in your Fruitful Cash Account, and the full statement balance must be paid off each month. This means your available balance reflects your card spending activity and you never carry over a credit balance or get charged interest.',
            },
            {
                question: 'How does AutoPay work?',
                answer: 'AutoPay means your statement balance is automatically paid off from your Fruitful Cash Account each month, so you never have to worry about missing a payment. It’s a simple, stress-free way to make sure your balance is covered on time, every time.',
            },
            {
                question:
                    'What is the minimum amount required to open a Fruitful Cash Account?',
                answer: 'There is no minimum deposit required to open an account, and you only need $0.01 to start earning interest with a Fruitful Cash Account.',
            },
            {
                question: 'Do you offer joint accounts?',
                answer: 'Yes, we offer both individual and joint deposit accounts.',
            },
            {
                question: 'Where can I use my Fruitful Card?',
                answer: 'You can use your Fruitful Card to make purchases everywhere Mastercard is accepted.',
            },
            {
                question: 'Can I use my Fruitful Card at the ATM?',
                answer: 'You can use your Fruitful Card at any ATM where Mastercard® is accepted. When withdrawing funds via cash advance, you can select “checking” for the account type when prompted by the ATM. Please also note that while Fruitful does not charge any fees for using an ATM, the ATM operators and/or Financial Institutions may do so.',
            },
        ],
    },
    {
        category: 'Banking at Fruitful',
        faqs: [
            {
                question: 'Is my money safe?',
                answer: 'Yes, Fruitful Cash Account(s) are held at Emigrant Bank, an FDIC-insured institution. While there, your funds are insured up to $250,000 and covered in the event of failure of only Emigrant Bank. Fruitful must meet and satisfy certain conditions for pass-through deposit insurance coverage in the event of Emigrant Bank’s failure.',
            },
            {
                question: "What's Emigrant Bank?",
                answer: 'Emigrant Bank is our sponsor bank. Because Fruitful is a financial technology company, not an FDIC-insured bank, we partner with Emigrant Bank to provide you Fruitful banking services. Emigrant Bank is also the issuer of the Fruitful Card pursuant to license by Mastercard International Incorporated.',
            },
            {
                question: 'When do I start earning interest on my account?',
                answer: 'The interest on your interest-bearing deposit account begins to accrue on the business day a money transfer is initiated from the Fruitful member portal to debit the external bank, or as soon as funds are received from an external bank account. If the money transfer is initiated outside of business hours, interest will begin accruing the next business day.',
            },
            {
                question: 'How is the interest paid?',
                answer: 'Interest compounds daily and calculated on a 365/366 day basis. Interest is credited to your Fruitful Cash Account on a monthly basis.',
            },
            {
                question:
                    'What is the difference between my interest rate and my APY?',
                answer: 'Interest Rate is the annualized rate applied to the principal balance of the account each day. The APY (Annual Percentage Yield) is the percentage rate reflecting the total amount of interest paid on an account, based on the interest rate and frequency of compounding. Unless otherwise noted, it is based on a 365-day period (366-day period in a leap year).',
            },
            {
                question:
                    'Why do you need my personal information when opening an account?',
                answer: 'To help the government fight the funding of terrorism and money laundering activities, Federal law requires all financial institutions to obtain, verify, and record information that identifies each person and/or entity that opens an account and to understand the anticipated activity of the account. What this means to you: When you open an account, we will ask for your name, physical address, date of birth and other information that will allow us to verify your identity. We may also ask to see your driver’s license or other identifying documents.',
            },
            {
                question:
                    "What do I do if I don't recognize a transaction in my account?",
                answer: `If you do not recognize a transaction on your Fruitful Card or in your Fruitful Cash Account, contact us immediately via email at ${SUPPORT_EMAIL}. Please provide your first and last name, the last 4 digits of the account with the error, the transaction description, date, and amount, if the transaction was on the Fruitful Card or Fruitful Cash Account, and a brief description as to why you believe an error has occurred. You may also include any supporting documentation related to the error(s).\n\nOur team will be in touch as soon as possible, either by calling you back or sending you an email. If you believe that your Card has been compromised, please log into your account at my.fruitful.com and report your card as lost or stolen right away.`,
            },
            {
                question: 'What if my provider doesn’t recognize Fruitful?',
                answer: <ProviderRecognitionFAQAnswer />,
            },
        ],
    },
    {
        category: 'My account & membership',
        faqs: [
            {
                question: 'How do I change my personal information?',
                answer: `You can email Fruitful Hospitality at ${SUPPORT_EMAIL}.`,
            },
            {
                question: 'How can I file a complaint?',
                answer: `If you wish to submit a complaint about a Fruitful product or service, you may do so by contacting ${SUPPORT_EMAIL}.`,
            },
            {
                question: 'How do I cancel my paid Fruitful Membership?',
                answer: 'You can cancel your paid Membership in the Fruitful app under Account Settings -> Membership. Cancelling means your Membership will not renew. Annual Memberships are billed through their Renewal Date.\n\nYou will continue to have access to your paid Membership and the applicable Services until your Renewal Date. After that date, you will retain Fruitful’s standard Membership benefits.',
            },
        ],
    },
];
